import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineView } from "foldkit/submodel";

import type { RecordHistoryEntry } from "../../api/schemas/records";
import type {
	Canonical,
	ScorecardResult,
	Task,
	TaskComment,
} from "../../api/schemas/tasks";
import { Route, linkHref } from "../../app/routes";
import { i18next } from "../../i18n";
import {
	type Priority,
	type Status,
	agentBadge,
	gradeChip,
	priorityGlyph,
	priorityLabel,
	relativeTime,
	statusChip,
	statusLabel,
} from "../../widgets/a2aUi";
import { busyAttributes, busySpinner } from "../../widgets/busy";
import * as Icons from "../../widgets/icons";
import { agentLabelText } from "../../widgets/provenance";
import type { Shared } from "../shared";
import { Message } from "./message";
import {
	COMPOSER_BODY_ID,
	type Loaded,
	type Model,
	type Subtask,
} from "./model";

type H = HtmlBuilder<Message>;

const SECTIONS = ["hypothesis", "evidence", "outcome"] as const;
type Section = (typeof SECTIONS)[number];

const sectionLabel = (section: Section): string =>
	i18next.t(`tasks.detail.section.${section}`);

// ---- Names ------------------------------------------------------------------

/**
 * Who an actor id is, by name: people who wrote a task version (history
 * provenance) or an agent's api key. Anyone else is a generic label: there is
 * no member directory, and a raw id is never shown.
 */
/** A sign-in email as a name ("sam.miller+1@x.io" → "sam.miller"); anything else as is. */
export const shortName = (name: string): string =>
	/^[^\s@]+@[^\s@]+$/.test(name) ? (name.split(/[+@]/)[0] ?? name) : name;

export type Names = ReadonlyMap<string, { name: string; agent: boolean }>;

export const namesOf = (data: Loaded): Names =>
	new Map<string, { name: string; agent: boolean }>([
		...(data.history ?? []).flatMap((v) => {
			const person = v.event.provenance?.person;
			return person?.name
				? [[person.id, { name: shortName(person.name), agent: false }] as const]
				: [];
		}),
		...data.keys.map(
			(k) =>
				[
					k.id,
					{ name: k.description ?? agentLabelText(null), agent: true },
				] as const,
		),
	]);

const nameText = (names: Names, id: string): string =>
	names.get(id)?.name ?? i18next.t("tasks.detail.someone");

const who = (h: H, names: Names, id: string): Html => {
	const known = names.get(id);
	return known?.agent
		? agentBadge(h, known.name)
		: h.span([h.Class("font-medium")], [nameText(names, id)]);
};

// ---- Writes -----------------------------------------------------------------

/** What the write buttons need; null under asOf, where nothing writable renders. */
type Writes = Readonly<{
	promoted: ReadonlySet<string>;
	saving: boolean;
	model: Model;
}> | null;

const writesOf = (shared: Shared, model: Model, task: Task): Writes =>
	Option.isSome(shared.asOf)
		? null
		: {
				promoted: new Set(
					SECTIONS.flatMap((s) =>
						(task.sections[s] ?? []).flatMap((c) =>
							c.promoted_from_comment_id ? [c.promoted_from_comment_id] : [],
						),
					),
				),
				saving: model.saving,
				model,
			};

/**
 * The last write's failure, beside the control that started it (`at` is
 * "composer" or a comment id). Solid, not soft: soft alerts tint the text in
 * the alert hue and fail contrast.
 */
const writeAlert = (h: H, model: Model, at: string): Html =>
	model.writeError === null || model.writeAt !== at
		? h.empty
		: h.p(
				[
					h.Role("alert"),
					h.DataAttribute("testid", "write-alert"),
					h.Class(
						`alert text-sm ${model.writeError === "conflict" ? "alert-warning" : "alert-error"}`,
					),
				],
				[
					i18next.t(
						model.writeError === "conflict"
							? "tasks.detail.actions.conflict"
							: "tasks.detail.actions.writeError",
					),
				],
			);

const smallButton = (
	h: H,
	w: NonNullable<Writes>,
	testid: string,
	label: string,
	text: string,
	message: Message,
): Html =>
	h.button(
		[
			h.Type("button"),
			h.DataAttribute("testid", testid),
			h.AriaLabel(label),
			h.Disabled(w.saving),
			h.OnClick(message),
			h.Class("btn btn-ghost btn-xs"),
		],
		[text],
	);

const commentActions = (h: H, w: Writes, comment: TaskComment): Html => {
	if (w === null) return h.empty;
	const section = SECTIONS.find((s) => s === comment.type);
	return h.div(
		[h.Class("-ml-2 flex flex-wrap gap-1")],
		[
			smallButton(
				h,
				w,
				"comment-reply",
				i18next.t("tasks.detail.actions.replyLabel"),
				i18next.t("tasks.detail.actions.reply"),
				Message.ClickedReply({ commentId: comment.id, type: comment.type }),
			),
			section === undefined || w.promoted.has(comment.id)
				? h.empty
				: smallButton(
						h,
						w,
						"comment-promote",
						i18next.t("tasks.detail.actions.promoteLabel", {
							section: sectionLabel(section),
						}),
						i18next.t("tasks.detail.actions.promote"),
						Message.ClickedPromote({ commentId: comment.id }),
					),
		],
	);
};

// ---- Threads ----------------------------------------------------------------

export type Thread = Readonly<{
	comment: TaskComment;
	replies: ReadonlyArray<Thread>;
}>;

/** Replies nest under their parent by `parent_comment_id`, oldest first at every level. */
export const threadsOf = (
	comments: ReadonlyArray<TaskComment>,
	roots: ReadonlyArray<TaskComment>,
): ReadonlyArray<Thread> => {
	const byAge = (list: ReadonlyArray<TaskComment>) =>
		[...list].sort((a, b) => a.created_at.localeCompare(b.created_at));
	const build = (comment: TaskComment, seen: ReadonlySet<string>): Thread => ({
		comment,
		replies: byAge(
			comments.filter(
				(c) => c.parent_comment_id === comment.id && !seen.has(c.id),
			),
		).map((c) => build(c, new Set([...seen, c.id]))),
	});
	return byAge(roots).map((c) => build(c, new Set([c.id])));
};

/** Top-level comments of one type; a reply whose parent is gone counts as top level. */
const rootsOf = (
	comments: ReadonlyArray<TaskComment>,
	type: TaskComment["type"],
	exclude: ReadonlySet<string> = new Set(),
) => {
	const ids = new Set(comments.map((c) => c.id));
	return comments.filter(
		(c) =>
			c.type === type &&
			!exclude.has(c.id) &&
			(c.parent_comment_id === null || !ids.has(c.parent_comment_id)),
	);
};

// ---- Activity ---------------------------------------------------------------

const parse = (value: unknown): Record<string, unknown> => {
	if (typeof value !== "string")
		return (value as Record<string, unknown>) ?? {};
	try {
		return (JSON.parse(value) as Record<string, unknown>) ?? {};
	} catch {
		return {};
	}
};

const same = (a: unknown, b: unknown) =>
	JSON.stringify(a) === JSON.stringify(b);

export type Activity = Readonly<{
	at: string;
	who: string | null;
	changes: ReadonlyArray<string>;
}>;

/** What each version changed against the one before it (history is newest first). */
export const activityOf = (
	history: ReadonlyArray<RecordHistoryEntry>,
): ReadonlyArray<Activity> =>
	history.map((entry, i) => {
		const older = history[i + 1];
		const provenance = entry.event.provenance;
		const person = provenance?.person?.name;
		const by =
			(person ? shortName(person) : null) ??
			(provenance?.agent ? agentLabelText(null) : null);
		const base = { at: entry.event.datetime, who: by };
		if (older === undefined)
			return {
				...base,
				changes: [
					i18next.t(
						entry.event.type.includes("create")
							? "tasks.detail.change.created"
							: "tasks.detail.change.edited",
					),
				],
			};
		const now = parse(entry.version.data);
		const was = parse(older.version.data);
		const pick = (key: string) => [was[key], now[key]] as const;
		const [fromStatus, toStatus] = pick("status");
		const [fromPriority, toPriority] = pick("priority");
		const nowSections = parse(now.sections);
		const wasSections = parse(was.sections);
		const changes = [
			...(fromStatus !== toStatus && typeof toStatus === "string"
				? [
						i18next.t("tasks.detail.change.status", {
							from: statusLabel(fromStatus as Status),
							to: statusLabel(toStatus as Status),
						}),
					]
				: []),
			...(fromPriority !== toPriority && typeof toPriority === "string"
				? [
						i18next.t("tasks.detail.change.priority", {
							from: priorityLabel(fromPriority as Priority),
							to: priorityLabel(toPriority as Priority),
						}),
					]
				: []),
			...(entry.version.name !== older.version.name
				? [i18next.t("tasks.detail.change.title")]
				: []),
			...(entry.version.description !== older.version.description
				? [i18next.t("tasks.detail.change.description")]
				: []),
			...SECTIONS.filter(
				(s) => !same(nowSections[s] ?? [], wasSections[s] ?? []),
			).map((s) => i18next.t(`tasks.detail.change.sections.${s}`)),
			...(["due", "assignee_id", "people"] as const)
				.filter((k) => !same(now[k] ?? null, was[k] ?? null))
				.map((k) => i18next.t(`tasks.detail.change.${k}`)),
			...(entry.version.tags !== older.version.tags
				? [i18next.t("tasks.detail.change.tags")]
				: []),
			...(entry.version.attributes !== older.version.attributes
				? [i18next.t("tasks.detail.change.attributes")]
				: []),
		];
		return {
			...base,
			changes:
				changes.length > 0
					? changes
					: [i18next.t("tasks.detail.change.edited")],
		};
	});

// ---- Pieces -----------------------------------------------------------------

const paragraphs = (h: H, text: string, lead = false): ReadonlyArray<Html> =>
	text
		.split(/\n\s*\n/)
		.filter((p) => p.trim() !== "")
		.map((p, i) => {
			// "Prod log sample, 24 h window: observed …" reads as a titled item.
			const match = lead && i === 0 ? /^([^:\n]{3,72}):\s/.exec(p) : null;
			const rest = match ? p.slice(match[0].length) : p;
			return match
				? h.div(
						[h.Class("flex flex-col gap-0.5")],
						[
							h.h3(
								[h.Class("font-semibold break-words")],
								// The colon stays: the text copies and reads as written.
								[`${match[1] ?? ""}:`],
							),
							h.p(
								[
									h.Class(
										"whitespace-pre-line break-words text-sm text-base-content/75",
									),
								],
								[rest],
							),
						],
					)
				: h.p([h.Class("whitespace-pre-line break-words")], [p]);
		});

const timeText = (h: H, iso: string, now: number): Html =>
	h.time(
		[h.Title(iso), h.Class("whitespace-nowrap")],
		[relativeTime(iso, now)],
	);

const commentView = (
	h: H,
	thread: Thread,
	names: Names,
	now: number,
	w: Writes,
): Html =>
	h.li(
		[h.DataAttribute("testid", "comment"), h.Class("flex flex-col gap-1")],
		[
			h.div(
				[
					h.Class(
						"flex flex-wrap items-center gap-x-2 text-xs text-base-content/60",
					),
				],
				[
					who(h, names, thread.comment.author_id),
					timeText(h, thread.comment.created_at, now),
				],
			),
			h.div(
				[h.Class("flex flex-col gap-2 text-sm")],
				paragraphs(h, thread.comment.body),
			),
			commentActions(h, w, thread.comment),
			w === null ? h.empty : writeAlert(h, w.model, thread.comment.id),
			thread.replies.length === 0
				? h.empty
				: h.ol(
						[
							h.DataAttribute("testid", "replies"),
							h.AriaLabel(i18next.t("tasks.detail.replies")),
							h.Class(
								"mt-2 flex flex-col gap-3 border-l-2 border-base-300 pl-4",
							),
						],
						thread.replies.map((r) => commentView(h, r, names, now, w)),
					),
		],
	);

const threadList = (
	h: H,
	threads: ReadonlyArray<Thread>,
	names: Names,
	now: number,
	label: string,
	w: Writes,
): Html =>
	h.ol(
		[h.AriaLabel(label), h.Class("flex flex-col gap-4")],
		threads.map((t) => commentView(h, t, names, now, w)),
	);

const card = (extra = "") =>
	`rounded-box border border-base-300 bg-base-100 ${extra}`;

const heading = (
	h: H,
	id: string,
	text: string,
	letter: string | null,
	note: string | null,
): Html =>
	h.div(
		[h.Class("flex items-baseline gap-2")],
		[
			letter === null
				? h.empty
				: h.span(
						[
							h.AriaHidden(true),
							h.Class(
								"badge badge-sm badge-soft badge-primary self-center font-mono font-semibold",
							),
						],
						[letter],
					),
			h.h2([h.Id(id), h.Class("font-display text-lg")], [text]),
			note === null
				? h.empty
				: h.span([h.Class("text-sm text-base-content/50")], [note]),
		],
	);

const unavailable = (h: H, text: string): Html =>
	h.p([h.Role("alert"), h.Class("text-sm text-error")], [text]);

const canonicalView = (
	h: H,
	section: Section,
	canonical: Canonical,
	comments: ReadonlyArray<TaskComment>,
	names: Names,
	now: number,
	w: Writes,
): Html => {
	const source =
		canonical.promoted_from_comment_id == null
			? undefined
			: comments.find((c) => c.id === canonical.promoted_from_comment_id);
	const replies = source
		? (threadsOf(comments, [source])[0]?.replies ?? [])
		: [];
	const at = canonical.promoted_at ?? source?.created_at ?? null;
	return h.li(
		[
			h.DataAttribute("testid", `canonical-${section}`),
			h.Class("flex flex-col gap-1 px-4 py-2.5"),
		],
		[
			h.div(
				[h.Class("flex flex-col gap-1.5 text-[0.9375rem] leading-snug")],
				paragraphs(h, canonical.text, section === "evidence"),
			),
			// Provenance and Demote share one line: the evidence, not its chrome, gets the height.
			h.div(
				[h.Class("flex flex-wrap items-center justify-between gap-x-3")],
				[
					h.div(
						[
							h.DataAttribute("testid", "canonical-provenance"),
							h.Class(
								"flex flex-wrap items-center gap-x-1.5 font-mono text-xs text-base-content/55",
							),
						],
						source
							? [
									who(h, names, source.author_id),
									h.span([h.AriaHidden(true)], ["·"]),
									h.span(
										[],
										[
											i18next.t("tasks.detail.promotedFromComment", {
												time: at === null ? "" : relativeTime(at, now),
											}),
										],
									),
								]
							: at === null
								? []
								: [
										h.span(
											[],
											[
												i18next.t("tasks.detail.pinned", {
													time: relativeTime(at, now),
												}),
											],
										),
									],
					),
					w === null || canonical.promoted_from_comment_id == null
						? h.empty
						: h.div(
								[h.Class("flex items-center gap-2")],
								[
									smallButton(
										h,
										w,
										"canonical-demote",
										i18next.t("tasks.detail.actions.demoteLabel", {
											section: sectionLabel(section),
										}),
										i18next.t("tasks.detail.actions.demote"),
										Message.ClickedDemote({
											section,
											commentId: canonical.promoted_from_comment_id,
										}),
									),
									writeAlert(h, w.model, canonical.promoted_from_comment_id),
								],
							),
				],
			),
			replies.length === 0
				? h.empty
				: threadList(
						h,
						replies,
						names,
						now,
						i18next.t("tasks.detail.replies"),
						w,
					),
		],
	);
};

const sectionView = (
	h: H,
	section: Section,
	task: Task,
	comments: ReadonlyArray<TaskComment> | null,
	names: Names,
	now: number,
	w: Writes,
): Html => {
	const canonicals = task.sections[section] ?? [];
	const promoted = new Set(
		canonicals.flatMap((c) =>
			c.promoted_from_comment_id ? [c.promoted_from_comment_id] : [],
		),
	);
	const threads =
		comments === null
			? []
			: threadsOf(comments, rootsOf(comments, section, promoted));
	const id = `task-section-${section}`;
	const letter = i18next.t(`tasks.detail.letter.${section}`);
	return h.section(
		[
			h.AriaLabelledBy(id),
			h.DataAttribute("testid", `section-${section}`),
			h.Class("flex flex-col gap-2"),
		],
		[
			heading(
				h,
				id,
				sectionLabel(section),
				letter,
				canonicals.length > 1
					? i18next.t("tasks.detail.items", { count: canonicals.length })
					: null,
			),
			canonicals.length === 0 && threads.length === 0
				? h.p(
						[
							h.Class(
								"rounded-box border border-dashed border-base-300 px-5 py-4 text-sm text-base-content/55",
							),
						],
						[i18next.t(`tasks.detail.empty.${section}`)],
					)
				: h.div(
						[h.Class(card("overflow-hidden"))],
						[
							canonicals.length === 0
								? h.empty
								: h.ol(
										[h.Class("divide-y divide-base-300")],
										canonicals.map((c) =>
											canonicalView(
												h,
												section,
												c,
												comments ?? [],
												names,
												now,
												w,
											),
										),
									),
							threads.length === 0
								? h.empty
								: h.div(
										[
											h.Class(
												`bg-base-200/50 px-5 py-4 ${canonicals.length === 0 ? "" : "border-t border-base-300"}`,
											),
										],
										[
											h.p(
												[
													h.Class(
														"mb-3 text-xs font-semibold uppercase tracking-wide text-base-content/55",
													),
												],
												[
													i18next.t("tasks.detail.sectionComments", {
														section: sectionLabel(section),
													}),
												],
											),
											threadList(
												h,
												threads,
												names,
												now,
												i18next.t("tasks.detail.sectionComments", {
													section: sectionLabel(section),
												}),
												w,
											),
										],
									),
						],
					),
		],
	);
};

const COMMENT_TYPES = ["discussion", ...SECTIONS] as const;

const typeLabel = (type: TaskComment["type"]): string =>
	type === "discussion"
		? i18next.t("tasks.detail.discussion")
		: sectionLabel(type);

/** One composer for new comments and replies; a reply names who it answers. */
const composer = (
	h: H,
	model: Model,
	comments: ReadonlyArray<TaskComment>,
	names: Names,
): Html => {
	const { draft, saving } = model;
	const parent = comments.find((c) => c.id === draft.replyTo);
	return h.form(
		[
			h.DataAttribute("testid", "comment-composer"),
			h.AriaLabel(i18next.t("tasks.detail.actions.composer")),
			h.OnSubmit(Message.SubmittedComment()),
			h.Class(card("flex flex-col gap-2 p-4")),
		],
		[
			parent === undefined
				? h.empty
				: h.div(
						[
							h.DataAttribute("testid", "replying-to"),
							h.Class("flex items-center gap-2 text-xs text-base-content/60"),
						],
						[
							h.span(
								[],
								[
									i18next.t("tasks.detail.actions.replyingTo", {
										name: nameText(names, parent.author_id),
									}),
								],
							),
							h.button(
								[
									h.Type("button"),
									h.AriaLabel(i18next.t("tasks.detail.actions.cancelReply")),
									h.OnClick(Message.ClickedCancelReply()),
									h.Class("btn btn-ghost btn-xs"),
								],
								[i18next.t("tasks.detail.actions.cancel")],
							),
						],
					),
			h.textarea([
				h.Id(COMPOSER_BODY_ID),
				h.AriaLabel(i18next.t("tasks.detail.actions.bodyLabel")),
				h.Placeholder(i18next.t("tasks.detail.actions.placeholder")),
				h.Rows(3),
				h.Value(draft.body),
				h.OnInput((body) => Message.ChangedDraft({ body })),
				h.Class("textarea textarea-bordered w-full text-sm"),
			]),
			h.div(
				[h.Class("flex flex-wrap items-center justify-end gap-2")],
				[
					h.select(
						[
							h.AriaLabel(i18next.t("tasks.detail.actions.typeLabel")),
							h.OnChange((value) =>
								Message.ChangedDraftType({
									type: COMMENT_TYPES.find((t) => t === value) ?? draft.type,
								}),
							),
							h.Class("select select-sm w-auto"),
						],
						COMMENT_TYPES.map((type) =>
							h.option(
								[h.Value(type), h.Selected(type === draft.type)],
								[typeLabel(type)],
							),
						),
					),
					h.button(
						[
							h.Type("submit"),
							h.AriaLabel(
								i18next.t(
									parent === undefined
										? "tasks.detail.actions.postLabel"
										: "tasks.detail.actions.replyPostLabel",
								),
							),
							...busyAttributes(h, saving, draft.body.trim() === ""),
							h.Class("btn btn-primary btn-sm"),
						],
						[busySpinner(h, saving), i18next.t("tasks.detail.actions.post")],
					),
				],
			),
			writeAlert(h, model, "composer"),
		],
	);
};

const discussionView = (
	h: H,
	model: Model,
	comments: ReadonlyArray<TaskComment> | null,
	names: Names,
	now: number,
	w: Writes,
): Html => {
	const threads =
		comments === null
			? []
			: threadsOf(comments, rootsOf(comments, "discussion"));
	return h.section(
		[
			h.AriaLabelledBy("task-discussion"),
			h.DataAttribute("testid", "discussion"),
			h.Class("flex flex-col gap-3"),
		],
		[
			heading(
				h,
				"task-discussion",
				i18next.t("tasks.detail.discussion"),
				null,
				null,
			),
			comments === null
				? unavailable(h, i18next.t("tasks.detail.commentsError"))
				: threads.length === 0
					? h.p(
							[h.Class("text-sm text-base-content/55")],
							[i18next.t("tasks.detail.noDiscussion")],
						)
					: h.div(
							[h.Class(card("px-5 py-4"))],
							[
								threadList(
									h,
									threads,
									names,
									now,
									i18next.t("tasks.detail.discussion"),
									w,
								),
							],
						),
			w === null || comments === null
				? h.empty
				: composer(h, model, comments, names),
		],
	);
};

const subtasksView = (
	h: H,
	shared: Shared,
	task: Task,
	subtasks: ReadonlyArray<Subtask> | null,
): Html => {
	const done = subtasks?.filter((s) => s.task.status === "done").length ?? 0;
	return h.section(
		[
			h.AriaLabelledBy("task-subtasks"),
			h.DataAttribute("testid", "subtasks"),
			h.Class("flex flex-col gap-2"),
		],
		[
			heading(
				h,
				"task-subtasks",
				i18next.t("tasks.detail.subtasks"),
				null,
				subtasks === null || subtasks.length === 0
					? null
					: i18next.t("tasks.detail.subtasksDone", {
							done,
							total: subtasks.length,
						}),
			),
			subtasks === null
				? unavailable(h, i18next.t("tasks.detail.subtasksError"))
				: subtasks.length === 0
					? h.p(
							[h.Class("text-sm text-base-content/55")],
							[i18next.t("tasks.detail.noSubtasks")],
						)
					: h.ul(
							[h.Class(card("divide-y divide-base-300"))],
							subtasks.map(({ task: sub, grade }) => {
								const closed =
									sub.status === "done" || sub.status === "canceled";
								return h.li(
									[
										h.DataAttribute("testid", "subtask"),
										h.Class(
											"flex items-center gap-3 px-4 py-2 max-sm:flex-wrap",
										),
									],
									[
										h.span(
											[
												h.Class("flex min-w-0 flex-1 items-center gap-2"),
												// Grandchildren step in.
												h.Style({
													"padding-left": `${Math.max(0, sub.depth - task.depth - 1) * 1.25}rem`,
												}),
											],
											[
												(closed ? Icons.CheckSquare : Icons.ChevronRight)(h, {
													size: 16,
													class: closed
														? "shrink-0 text-success"
														: "shrink-0 text-base-content/40",
												}),
												h.a(
													[
														h.Href(
															linkHref(
																shared.route,
																Route.Task({ id: sub.id, asOf: shared.asOf }),
															),
														),
														h.Class(
															`link link-hover min-w-0 break-words ${closed ? "text-base-content/60" : "font-medium"} ${sub.status === "canceled" ? "line-through" : ""}`,
														),
													],
													[sub.title],
												),
											],
										),
										h.span(
											[h.Class("flex shrink-0 items-center gap-2 max-sm:ml-6")],
											[
												statusChip(h, sub.status),
												grade === null
													? h.span(
															[
																h.Class("text-base-content/40"),
																h.Role("img"),
																h.AriaLabel(i18next.t("a2aUi.grade.ungraded")),
															],
															["—"],
														)
													: gradeChip(h, grade),
											],
										),
									],
								);
							}),
						),
		],
	);
};

// ---- Sidebar ----------------------------------------------------------------

/** A criterion bar: red only for a real weakness (below 5), so a middling 5.5 reads amber. */
const barClass = (score: number | null): string =>
	score === null
		? "bg-base-300"
		: score >= 7
			? "bg-success"
			: score >= 5
				? "bg-warning"
				: "bg-error";

const panelTitle = (h: H, id: string, text: string): Html =>
	h.h2(
		[
			h.Id(id),
			h.Class(
				"text-xs font-semibold uppercase tracking-wider text-base-content/55",
			),
		],
		[text],
	);

const scorecardView = (h: H, data: Loaded, now: number): Html => {
	const runs = [...(data.scorecard ?? [])].sort((a, b) =>
		a.reviewed_at.localeCompare(b.reviewed_at),
	);
	const latest: ScorecardResult | undefined = runs.at(-1);
	const previous: ScorecardResult | undefined = runs.at(-2);
	const config = data.configs.find((c) => c.id === latest?.config_id);
	const criteria = latest
		? (
				config?.criteria.map((c) => ({ key: c.key, label: c.label })) ?? []
			).concat(
				Object.keys(latest.scores)
					.filter((k) => !config?.criteria.some((c) => c.key === k))
					.map((k) => ({ key: k, label: k })),
			)
		: [];
	const shown = (n: number) => (Math.round(n * 10) / 10).toFixed(1);
	return h.section(
		[
			h.AriaLabelledBy("task-scorecard"),
			h.DataAttribute("testid", "scorecard"),
			h.Class(card("overflow-hidden")),
		],
		[
			h.div(
				[
					h.Class(
						"flex items-start justify-between gap-3 bg-base-200/60 px-5 py-4",
					),
				],
				[
					h.div(
						[h.Class("min-w-0")],
						[
							panelTitle(
								h,
								"task-scorecard",
								i18next.t("tasks.detail.scorecard"),
							),
							config
								? h.p(
										[
											h.Class(
												"mt-1 break-words font-mono text-xs text-base-content/60",
											),
										],
										[config.name],
									)
								: h.empty,
						],
					),
					latest
						? h.p(
								[
									h.DataAttribute("testid", "scorecard-grade"),
									h.AriaLabel(
										i18next.t("tasks.detail.latestGrade", {
											grade: shown(latest.overall_grade),
										}),
									),
									h.Class("shrink-0 text-right leading-none"),
								],
								[
									h.span(
										[
											h.AriaHidden(true),
											h.Class(
												"mb-1 block text-xs font-medium text-base-content/60",
											),
										],
										[i18next.t("tasks.detail.overallGrade")],
									),
									h.span(
										[
											h.Class(
												"font-display text-4xl font-semibold tabular-nums",
											),
										],
										[shown(latest.overall_grade)],
									),
									h.span(
										[h.Class("ml-1 font-mono text-xs text-base-content/50")],
										[i18next.t("tasks.detail.outOfTen")],
									),
									previous
										? h.span(
												[
													h.DataAttribute("testid", "scorecard-previous"),
													h.Class(
														"mt-1 block font-mono text-xs text-base-content/55",
													),
												],
												[
													i18next.t("tasks.detail.previousGrade", {
														grade: shown(previous.overall_grade),
													}),
												],
											)
										: h.empty,
								],
							)
						: h.empty,
				],
			),
			data.scorecard === null
				? h.div(
						[h.Class("px-5 py-4")],
						[unavailable(h, i18next.t("tasks.detail.scorecardError"))],
					)
				: latest === undefined
					? h.p(
							[h.Class("px-5 py-4 text-sm text-base-content/55")],
							[i18next.t("tasks.detail.notGraded")],
						)
					: h.div(
							[],
							[
								h.ul(
									[
										h.Class(
											"divide-y divide-base-300 border-t border-base-300",
										),
									],
									criteria.map(({ key, label }) => {
										const score = latest.scores[key];
										const value = score?.score ?? 0;
										return h.li(
											[
												h.DataAttribute("testid", "criterion"),
												h.Class("flex flex-col gap-1.5 px-5 py-3"),
											],
											[
												h.div(
													[
														h.Class(
															"flex items-baseline justify-between gap-3",
														),
													],
													[
														h.span(
															[
																h.Class(
																	"min-w-0 break-words text-sm font-medium",
																),
															],
															[label],
														),
														h.span(
															[
																h.Class(
																	"font-mono text-sm font-semibold tabular-nums",
																),
															],
															[score ? shown(value) : "—"],
														),
													],
												),
												h.div(
													[
														h.Role("img"),
														h.AriaLabel(
															i18next.t("tasks.detail.criterionScore", {
																label,
																score: score ? shown(value) : "—",
															}),
														),
														h.Class(
															"h-1.5 w-full overflow-hidden rounded-full bg-base-300",
														),
													],
													[
														h.div(
															[
																h.Class(
																	`h-full rounded-full ${barClass(score ? value : null)}`,
																),
																h.Style({
																	width: `${Math.min(100, Math.max(0, value * 10))}%`,
																}),
															],
															[],
														),
													],
												),
												score?.reasoning
													? h.p(
															[
																h.Class(
																	"text-xs leading-relaxed text-base-content/65",
																),
															],
															[score.reasoning],
														)
													: h.empty,
											],
										);
									}),
								),
								h.div(
									[h.Class("border-t border-base-300 px-5 py-3")],
									[
										h.h3(
											[
												h.Class(
													"mb-2 text-xs font-semibold uppercase tracking-wider text-base-content/55",
												),
											],
											[i18next.t("tasks.detail.gradeHistory")],
										),
										h.ol(
											[
												h.DataAttribute("testid", "grade-history"),
												h.Class("flex flex-col gap-2"),
											],
											runs.map((run) =>
												h.li(
													[h.Class("flex items-start gap-2 text-xs")],
													[
														gradeChip(h, run.overall_grade),
														h.span(
															[
																h.Class(
																	"min-w-0 flex-1 break-words text-base-content/70",
																),
															],
															[run.summary],
														),
														h.span(
															[h.Class("shrink-0 text-base-content/50")],
															[timeText(h, run.reviewed_at, now)],
														),
													],
												),
											),
										),
									],
								),
							],
						),
		],
	);
};

const row = (h: H, label: string, value: Html | string): Html =>
	h.div(
		[h.Class("flex items-baseline justify-between gap-4 py-1.5")],
		[
			h.dt([h.Class("shrink-0 text-base-content/60")], [label]),
			h.dd([h.Class("min-w-0 break-words text-right")], [value]),
		],
	);

const dueText = (due: string): string => {
	const ms = Date.parse(due.length === 10 ? `${due}T00:00:00Z` : due);
	return Number.isNaN(ms)
		? due
		: new Intl.DateTimeFormat(i18next.language, {
				timeZone: "UTC",
				month: "short",
				day: "numeric",
				year: "numeric",
			}).format(ms);
};

/** One Details fact: a small label over its value, two to a row so the card stays short. */
const cell = (h: H, label: string, value: Html | string): Html =>
	h.div(
		[h.Class("flex min-w-0 flex-col gap-0.5")],
		[
			h.dt([h.Class("text-xs text-base-content/55")], [label]),
			h.dd([h.Class("min-w-0 break-words")], [value]),
		],
	);

const detailsView = (h: H, data: Loaded, names: Names): Html => {
	const { task } = data;
	const people = task.people.map(
		(id) => data.people[id] ?? i18next.t("tasks.detail.unknownPerson"),
	);
	return h.section(
		[
			h.AriaLabelledBy("task-details"),
			h.Class(card("flex flex-col gap-3 px-5 py-4")),
		],
		[
			panelTitle(h, "task-details", i18next.t("tasks.detail.details")),
			h.dl(
				[
					h.DataAttribute("testid", "task-details"),
					h.Class("grid grid-cols-2 gap-x-4 gap-y-3 text-sm"),
				],
				[
					cell(h, i18next.t("tasks.detail.status"), statusChip(h, task.status)),
					cell(
						h,
						i18next.t("tasks.detail.priority"),
						priorityGlyph(h, task.priority),
					),
					cell(
						h,
						i18next.t("tasks.detail.assignee"),
						task.assignee_id === null
							? i18next.t("tasks.detail.unassigned")
							: who(h, names, task.assignee_id),
					),
					cell(
						h,
						i18next.t("tasks.detail.due"),
						task.due === null ? "—" : dueText(task.due),
					),
					cell(
						h,
						i18next.t("tasks.detail.people"),
						people.length === 0 ? "—" : people.join(", "),
					),
					cell(
						h,
						i18next.t("tasks.detail.depth"),
						task.depth === 0
							? i18next.t("tasks.detail.topLevel")
							: String(task.depth),
					),
				],
			),
		],
	);
};

const attributeValue = (value: unknown): string =>
	value !== null && typeof value === "object" && "value" in value
		? String((value as { value: unknown }).value)
		: typeof value === "string"
			? value
			: JSON.stringify(value);

const attributesView = (h: H, task: Task): Html => {
	const entries = Object.entries(task.attributes ?? {});
	return h.section(
		[
			h.AriaLabelledBy("task-attributes"),
			h.Class(card("flex flex-col gap-2 px-5 py-4")),
		],
		[
			panelTitle(h, "task-attributes", i18next.t("tasks.detail.attributes")),
			entries.length === 0
				? h.p(
						[h.Class("text-sm text-base-content/55")],
						[i18next.t("tasks.detail.noAttributes")],
					)
				: h.dl(
						[
							h.DataAttribute("testid", "task-attributes"),
							h.Class("divide-y divide-base-300 text-sm"),
						],
						entries.map(([key, value]) =>
							row(
								h,
								key.replace(/_/g, " "),
								h.span([h.Class("font-mono text-xs")], [attributeValue(value)]),
							),
						),
					),
		],
	);
};

const activityView = (
	h: H,
	history: ReadonlyArray<RecordHistoryEntry> | null,
	now: number,
): Html =>
	h.section(
		[
			h.AriaLabelledBy("task-activity"),
			h.Class(card("flex flex-col gap-3 px-5 py-4")),
		],
		[
			panelTitle(h, "task-activity", i18next.t("tasks.detail.activity")),
			history === null
				? unavailable(h, i18next.t("tasks.detail.activityError"))
				: h.ol(
						[
							h.DataAttribute("testid", "activity"),
							h.Class(
								"flex flex-col gap-3 border-l border-base-300 pl-4 text-sm",
							),
						],
						activityOf(history).map((a) =>
							h.li(
								[h.Class("relative flex flex-col")],
								[
									h.span(
										[
											h.AriaHidden(true),
											h.Class(
												"absolute -left-[1.3rem] top-1.5 size-2 rounded-full bg-base-300 ring-2 ring-base-100",
											),
										],
										[],
									),
									h.span([h.Class("break-words")], [a.changes.join(" · ")]),
									h.span(
										[h.Class("text-xs text-base-content/55")],
										[
											a.who === null
												? relativeTime(a.at, now)
												: i18next.t("tasks.detail.byAt", {
														name: a.who,
														time: relativeTime(a.at, now),
													}),
										],
									),
								],
							),
						),
					),
		],
	);

// ---- Page -------------------------------------------------------------------

const rescoreButton = (h: H, rescore: Model["rescore"]): Html =>
	h.button(
		[
			h.Type("button"),
			h.DataAttribute("testid", "task-rescore"),
			h.AriaLabel(i18next.t("tasks.detail.rescoreLabel")),
			h.Disabled(rescore === "running"),
			h.OnClick(Message.ClickedRescore()),
			h.Class("btn btn-sm btn-primary shrink-0 sm:ml-auto"),
		],
		[
			rescore === "running"
				? h.span(
						[h.AriaHidden(true), h.Class("loading loading-spinner loading-xs")],
						[],
					)
				: Icons.RefreshCw(h, { size: 14 }),
			i18next.t(
				rescore === "running"
					? "tasks.detail.rescoring"
					: "tasks.detail.rescore",
			),
		],
	);

const RESCORE_ALERT = {
	failed: "tasks.detail.rescoreError",
	aiDisabled: "tasks.detail.actions.aiDisabled",
	aiNotConfigured: "tasks.detail.actions.aiNotConfigured",
	upstream: "tasks.detail.actions.upstream",
} as const;

const header = (
	h: H,
	shared: Shared,
	model: Model,
	data: Loaded,
	names: Names,
): Html => {
	const { task, parent } = data;
	const { now } = model;
	const writable = Option.isNone(shared.asOf);
	const latest = [...(data.scorecard ?? [])]
		.sort((a, b) => a.reviewed_at.localeCompare(b.reviewed_at))
		.at(-1);
	const crumb = (href: string, text: string) =>
		h.li([], [h.a([h.Href(href), h.Class("link link-hover")], [text])]);
	const parentHref =
		parent === null
			? null
			: linkHref(
					shared.route,
					Route.Task({ id: parent.id, asOf: shared.asOf }),
				);
	return h.header(
		[h.Class("flex min-w-0 flex-col gap-2 lg:col-start-1 lg:row-start-1")],
		[
			h.nav(
				[
					h.AriaLabel(i18next.t("tasks.detail.breadcrumb")),
					h.Class("breadcrumbs py-0 text-sm text-base-content/70"),
				],
				[
					h.ul(
						[],
						[
							crumb(
								linkHref(shared.route, Route.Tasks({ asOf: shared.asOf })),
								i18next.t("tasks.title"),
							),
							parent === null || parentHref === null
								? h.empty
								: crumb(parentHref, parent.title),
						],
					),
				],
			),
			h.h1(
				[h.Class("font-display text-2xl leading-tight break-words")],
				[task.title],
			),
			task.description === null || task.description === ""
				? h.empty
				: h.p(
						[h.Class("max-w-prose break-words text-base-content/70")],
						[task.description],
					),
			h.div(
				[
					h.DataAttribute("testid", "task-meta"),
					h.Class(
						"flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-base-content/70",
					),
				],
				[
					// From lg the Details card beside the title shows these; no need to say them twice.
					h.span(
						[h.Class("flex items-center gap-x-3 lg:hidden")],
						[statusChip(h, task.status), priorityGlyph(h, task.priority)],
					),
					// On a phone the scorecard sits at the foot of the page; its grade leads here.
					latest === undefined
						? h.empty
						: h.a(
								[
									h.Href("#task-scorecard"),
									h.AriaLabel(
										i18next.t("tasks.detail.latestGrade", {
											grade: latest.overall_grade.toFixed(1),
										}),
									),
									h.Class("lg:hidden"),
								],
								[gradeChip(h, latest.overall_grade)],
							),
					parent === null || parentHref === null
						? h.empty
						: h.span(
								[h.DataAttribute("testid", "task-parent")],
								[
									`${i18next.t("tasks.detail.parent")}: `,
									h.a(
										[
											h.Href(parentHref),
											h.Class("link link-hover font-medium"),
										],
										[parent.title],
									),
								],
							),
					// Who opened it and the tags are context, not the task: a phone skips them.
					h.span(
						[h.Class("max-sm:hidden")],
						[
							i18next.t("tasks.detail.opened", {
								time: relativeTime(task.created_at, now),
								name: nameText(names, task.created_by),
							}),
						],
					),
					...task.tags.map((tag) =>
						h.span(
							[h.Class("badge badge-sm badge-ghost font-mono max-sm:hidden")],
							[`#${tag}`],
						),
					),
					// The one action a reader takes from here: a fresh grade after new evidence.
					writable ? rescoreButton(h, model.rescore) : h.empty,
				],
			),
			!writable || model.rescore === "idle" || model.rescore === "running"
				? h.empty
				: h.p(
						[
							h.Role("alert"),
							h.DataAttribute("testid", "rescore-alert"),
							h.Class("alert alert-error text-sm"),
						],
						[i18next.t(RESCORE_ALERT[model.rescore])],
					),
		],
	);
};

const body = (h: H, shared: Shared, model: Model, data: Loaded): Html => {
	const names = namesOf(data);
	const { now } = model;
	const w = writesOf(shared, model, data.task);
	// Wide: the sidebar starts level with the title, so Details and the
	// scorecard sit above the fold. Narrow: header, the work, then the sidebar.
	return h.div(
		[h.Class("grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]")],
		[
			header(h, shared, model, data, names),
			h.div(
				[h.Class("flex min-w-0 flex-col gap-4 lg:col-start-1")],
				[
					// Subtasks follow the evidence so both sit above the fold; the outcome comes once there is one.
					...SECTIONS.flatMap((s) => [
						sectionView(h, s, data.task, data.comments, names, now, w),
						...(s === "evidence"
							? [subtasksView(h, shared, data.task, data.subtasks)]
							: []),
					]),
					discussionView(h, model, data.comments, names, now, w),
				],
			),
			h.aside(
				[
					h.AriaLabel(i18next.t("tasks.detail.sidebar")),
					h.Class(
						"flex min-w-0 flex-col gap-5 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-start",
					),
				],
				[
					detailsView(h, data, names),
					scorecardView(h, data, now),
					attributesView(h, data.task),
					activityView(h, data.history, now),
				],
			),
		],
	);
};

export const view = defineView<Model, Message, Shared>((model, shared, h) => {
	const data = AsyncData.getOrElse(model.detail, () => null);
	return h.article(
		[
			h.DataAttribute("testid", "page-Task"),
			h.Class("mx-auto flex w-full max-w-6xl min-w-0 flex-col gap-4"),
		],
		[
			Option.isSome(shared.asOf)
				? h.p(
						[
							h.DataAttribute("testid", "task-live-notice"),
							h.Class("alert alert-info alert-soft text-sm"),
						],
						[i18next.t("tasks.detail.liveNotice")],
					)
				: h.empty,
			data !== null
				? body(h, shared, model, data)
				: AsyncData.isFailure(model.detail)
					? h.div(
							[h.Role("alert"), h.Class("alert alert-error")],
							[h.span([], [i18next.t("tasks.detail.loadError")])],
						)
					: h.div(
							[
								h.Role("status"),
								h.Class("flex items-center gap-2 py-8 text-base-content/70"),
							],
							[
								Icons.Loader2(h, { size: 20, class: "animate-spin" }),
								h.span([], [i18next.t("tasks.detail.loading")]),
							],
						),
		],
	);
});
